import { createSlice } from "@reduxjs/toolkit";
import {
  registerDoctor,
  loginDoctor,
  verifyCodeDoctor,
  resendCodeDoctor,
  requestResetDoctor,
  resetPasswordDoctor,
  getCurrentDoctor,
  fetchDoctors,
  fetchDoctorById,
  searchDoctorsByName,
  updateDoctor,
  deleteDoctor,
} from "../thunks/doctorsThunks";
import Cookies from "js-cookie";

const normalizeDoctorStatus = (value) => {
  const s = String(value || "").trim().toLowerCase();

  if (["pending", "approved", "rejected"].includes(s)) {
    return s;
  }

  return null;
};

const getStatusFromPayload = (payload) => {
  return normalizeDoctorStatus(
    payload?.status ||
      payload?.raw?.status ||
      payload?.doctor?.status ||
      payload?.user?.status ||
      payload?.validationStatus ||
      payload?.accountStatus ||
      payload?.adminStatus ||
      payload?.raw?.validationStatus ||
      payload?.raw?.accountStatus ||
      payload?.raw?.adminStatus
  );
};

const getRejectionReasonFromPayload = (payload) => {
  return (
    payload?.rejectionReason ||
    payload?.raw?.rejectionReason ||
    payload?.rejectReason ||
    payload?.raw?.rejectReason ||
    payload?.reason ||
    payload?.raw?.reason ||
    payload?.adminRejectionReason ||
    payload?.raw?.adminRejectionReason ||
    null
  );
};

const initialState = {
  registrationForm: {
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    specialty: "",
    experience: "",
    practicePlace: "",
    country: "",
    city: "",
    clinicAddress: "",
    licenceImg: null,
    identityImg: null,
    avatar: null,
  },

  registrationLoading: false,
  registrationError: null,
  registrationSuccess: false,

  email: "",
  loading: false,
  error: "",
  codeSent: false,
  user: null,
  accessToken: null,
  message: "",

  doctors: [],
  selectedDoctor: null,
  doctorsLoaded: false,

  _lastGetCurrentDoctorRequestId: null,
};

const doctorSlice = createSlice({
  name: "doctor",
  initialState,

  reducers: {
    setField: (state, action) => {
      const { key, value } = action.payload;
      state.registrationForm[key] = value;
    },

    resetRegistrationForm: (state) => {
      state.registrationForm = { ...initialState.registrationForm };
      state.registrationLoading = false;
      state.registrationError = null;
      state.registrationSuccess = false;
    },

    clearError: (state) => {
      state.error = "";
      state.registrationError = null;
    },

    clearMessage: (state) => {
      state.message = "";
    },

    restoreSessionFromStorage: (state) => {
      try {
        const saved =
          localStorage.getItem("doctorSession") || Cookies.get("doctorSession");

        if (saved) {
          const { user, email } = JSON.parse(saved);

          if (user && !state.user) {
            const {
              status,
              rejectionReason,
              validatedAt,
              raw,
              ...safeUser
            } = user;

            state.user = safeUser;
          }

          if (email) {
            state.email = email;
          }
        }

        const token =
          Cookies.get("doctorAccessToken") ||
          Cookies.get("doctorToken") ||
          Cookies.get("accessToken") ||
          localStorage.getItem("doctorAccessToken") ||
          localStorage.getItem("doctorToken");

        if (token) {
          state.accessToken = token;
        }
      } catch (e) {
        console.error("Failed to restore session:", e);
      }
    },

    clearErrorKeepSession: (state) => {
      state.error = "";
    },

    logoutDoctorLocal: (state) => {
      state.user = null;
      state.email = "";
      state.error = "";
      state.message = "";
      state.codeSent = false;
      state.loading = false;
      state.accessToken = null;
      state.doctors = [];
      state.selectedDoctor = null;
      state.doctorsLoaded = false;
      state._lastGetCurrentDoctorRequestId = null;

      try {
        const doctorKeys = [
          "doctorAccessToken",
          "doctorToken",
          "doctor_token",
          "doctorSession",
          "doctor_id",
          "doctorId",
          "userRole",
          "activeSessionRole",
        ];
        Cookies.remove("doctorAccessToken");
        Cookies.remove("doctorToken");
        Cookies.remove("doctorSession");
        Cookies.remove("doctor_token");
        Cookies.remove("doctor_id");

        doctorKeys.forEach((key) => {
          Cookies.remove(key, { path: "/" });
          Cookies.remove(key, { path: "/", domain: ".mediwave.tn" });
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        });

        sessionStorage.removeItem("activeSessionRole");
        localStorage.removeItem("userRole");
      } catch (_) {}
    },
  },

  extraReducers: (builder) => {
    builder
      /* ========== REGISTER DOCTOR ========== */
      .addCase(registerDoctor.pending, (state) => {
        state.registrationLoading = true;
        state.registrationError = null;
        state.registrationSuccess = false;
      })

      .addCase(registerDoctor.fulfilled, (state) => {
        state.registrationLoading = false;
        state.registrationSuccess = true;
      })

      .addCase(registerDoctor.rejected, (state, action) => {
        state.registrationLoading = false;
        state.registrationError = action.payload || "Registration failed";
      })

      /* ========== LOGIN DOCTOR ========== */
      .addCase(loginDoctor.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.message = "";
        state.user = null;
      })

      .addCase(loginDoctor.fulfilled, (state, action) => {
        state.loading = false;

        const payload = action.payload || {};
        const loginDoctorData = payload.doctor || null;

        state.email = payload.email || loginDoctorData?.email || state.email;
        state.codeSent = !!payload.codeSent;
        state.message = payload.message || "";

        if (payload.accessToken) {
          state.accessToken = payload.accessToken;
        }

        if (loginDoctorData) {
          const {
            status,
            rejectionReason,
            validatedAt,
            raw,
            ...safeLoginDoctor
          } = loginDoctorData;

          // Important :
          // Le login peut contenir un ancien status.
          // On ne met PAS "pending" ici.
          // Le vrai status viendra de getCurrentDoctor /me.
          state.user = {
            ...(state.user || {}),
            ...safeLoginDoctor,
            isVerified: payload.isVerified ?? loginDoctorData.isVerified ?? false,
          };
        }
      })

      .addCase(loginDoctor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
      })

      /* ========== VERIFY CODE ========== */
      .addCase(verifyCodeDoctor.pending, (state) => {
        state.loading = true;
        state.error = "";
      })

      .addCase(verifyCodeDoctor.fulfilled, (state, action) => {
        state.loading = false;

        const payloadUser = action.payload?.user || action.payload?.doctor || null;

        if (payloadUser) {
          const {
            status,
            rejectionReason,
            validatedAt,
            raw,
            ...safePayloadUser
          } = payloadUser;

          // Même logique : ne pas écraser avec un vieux pending.
          state.user = {
            ...(state.user || {}),
            ...safePayloadUser,
          };
        }

        if (action.payload?.accessToken) {
          state.accessToken = action.payload.accessToken;
        }

        state.codeSent = false;
      })

      .addCase(verifyCodeDoctor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Invalid code";
      })

      /* ========== RESEND CODE ========== */
      .addCase(resendCodeDoctor.pending, (state) => {
        state.loading = true;
        state.error = "";
      })

      .addCase(resendCodeDoctor.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload;
      })

      .addCase(resendCodeDoctor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Resend failed";
      })

      /* ========== REQUEST PASSWORD RESET ========== */
      .addCase(requestResetDoctor.pending, (state) => {
        state.loading = true;
        state.error = "";
      })

      .addCase(requestResetDoctor.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.codeSent = true;
      })

      .addCase(requestResetDoctor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Request reset failed";
      })

      /* ========== RESET PASSWORD ========== */
      .addCase(resetPasswordDoctor.pending, (state) => {
        state.loading = true;
        state.error = "";
      })

      .addCase(resetPasswordDoctor.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.codeSent = false;
      })

      .addCase(resetPasswordDoctor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Password reset failed";
      })

      /* ========== GET CURRENT DOCTOR ========== */
      .addCase(getCurrentDoctor.pending, (state, action) => {
        state.loading = true;
        state.error = "";
        state._lastGetCurrentDoctorRequestId = action.meta.requestId;
      })

      .addCase(getCurrentDoctor.fulfilled, (state, action) => {
        if (action.meta.requestId !== state._lastGetCurrentDoctorRequestId) {
          return;
        }

        state.loading = false;
        state.error = "";

        const payload = action.payload || {};
        const status = getStatusFromPayload(payload) || "pending";
        const rejectionReason = getRejectionReasonFromPayload(payload);

        state.user = {
          ...(state.user || {}),
          ...payload,

          // Source officielle : /me
          status,
          rejectionReason,

          raw: {
            ...(payload.raw || {}),
            status,
            rejectionReason,
          },
        };

        state.email = payload?.email || state.email || "";

      })

      .addCase(getCurrentDoctor.rejected, (state, action) => {
        if (action.meta.requestId !== state._lastGetCurrentDoctorRequestId) {
          return;
        }

        state.loading = false;
        state.error = action.payload || "Unable to fetch current user";
      })

      /* ========== FETCH DOCTORS ========== */
      .addCase(fetchDoctors.pending, (state) => {
        state.loading = true;
        state.error = "";
      })

      .addCase(fetchDoctors.fulfilled, (state, action) => {
        state.loading = false;
        state.doctors = action.payload || [];
        state.doctorsLoaded = true;
      })

      .addCase(fetchDoctors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch doctors";
        state.doctorsLoaded = true;
      })

      /* ========== FETCH DOCTOR BY ID ========== */
      .addCase(fetchDoctorById.pending, (state) => {
        state.loading = true;
        state.error = "";
      })

      .addCase(fetchDoctorById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDoctor = action.payload || null;
      })

      .addCase(fetchDoctorById.rejected, (state, action) => {
        state.loading = false;
        state.selectedDoctor = null;
        state.error = action.payload || "Failed to fetch doctor profile";
      })

      /* ========== SEARCH DOCTORS ========== */
      .addCase(searchDoctorsByName.pending, (state) => {
        state.loading = true;
        state.error = "";
      })

      .addCase(searchDoctorsByName.fulfilled, (state, action) => {
        state.loading = false;
        state.doctors = action.payload || [];
      })

      .addCase(searchDoctorsByName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Erreur recherche";
      })

      /* ========== UPDATE DOCTOR ========== */
      .addCase(updateDoctor.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.message = "";
      })

      .addCase(updateDoctor.fulfilled, (state, action) => {
        state.loading = false;

        const updated = action.payload || {};
        const updatedStatus = getStatusFromPayload(updated);
        const updatedRejectionReason = getRejectionReasonFromPayload(updated);

        state.user = {
          ...(state.user || {}),
          ...updated,

          // Si updateDoctor ne renvoie pas le status, on garde celui de /me.
          status: updatedStatus || state.user?.status || null,

          rejectionReason:
            updatedRejectionReason || state.user?.rejectionReason || null,

          avatar: updated.avatar ?? state.user?.avatar ?? null,

          raw: {
            ...(state.user?.raw || {}),
            ...(updated.raw || {}),
            status: updatedStatus || state.user?.status || null,
            rejectionReason:
              updatedRejectionReason || state.user?.rejectionReason || null,
          },
        };

        state.message = "Profile updated successfully";
      })

      .addCase(updateDoctor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update doctor";
      })

      /* ========== DELETE DOCTOR ========== */
      .addCase(deleteDoctor.pending, (state) => {
        state.loading = true;
        state.error = "";
      })

      .addCase(deleteDoctor.fulfilled, (state, action) => {
        state.loading = false;
        state.user = null;
        state.message = action.payload || "Doctor deleted successfully";
      })

      .addCase(deleteDoctor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete doctor";
      });
  },
});

export const {
  setField,
  resetRegistrationForm,
  clearError,
  clearMessage,
  restoreSessionFromStorage,
  clearErrorKeepSession,
  logoutDoctorLocal,
} = doctorSlice.actions;

export default doctorSlice.reducer;